import { BooleanField, DateTimeField, RadioField, TextField } from "@hex-insights/forms";
import { StudentBehaviorNoteFormat } from "../../Format";
import { StudentBehaviorNoteFormValues } from "../../FormUtilities";
import { BaseFieldProps } from "../shared";
import { StudentNoteSelectField } from "../StudentNote";

/**
 * Generic props for fields of the StudentBehaviorNote model.
 */
type FieldProps<K extends keyof StudentBehaviorNoteFormValues.Base = keyof StudentBehaviorNoteFormValues.Base> =
	BaseFieldProps<Pick<StudentBehaviorNoteFormValues.Base, K>>;

/**
 * Field component for the `incidentTime` field of the StudentBehaviorNote model.
 */
export function IncidentTime({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.incidentTime,
	...props
}: FieldProps<"incidentTime">) {
	return (
		<DateTimeField formState={formState} name="incidentTime" label={label} optional precision="minute" {...props} />
	);
}

/**
 * Field component for the `incidentLocation` field of the StudentBehaviorNote model.
 */
export function IncidentLocation({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.incidentLocation,
	...props
}: FieldProps<"incidentLocation">) {
	return (
		<RadioField
			formState={formState}
			name="incidentLocation"
			label={label}
			options={StudentBehaviorNoteFormValues.incidentLocationOptions}
			blankValue={null}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `isIncidentPhysicalContactAggression` field of the StudentBehaviorNote model.
 */
export function IsIncidentPhysicalContactAggression({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentPhysicalContactAggression,
	...props
}: FieldProps<"isIncidentPhysicalContactAggression">) {
	return <BooleanField formState={formState} name="isIncidentPhysicalContactAggression" label={label} {...props} />;
}

/**
 * Field component for the `isIncidentFightingPhysicalAggression` field of the StudentBehaviorNote model.
 */
export function IsIncidentFightingPhysicalAggression({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentFightingPhysicalAggression,
	...props
}: FieldProps<"isIncidentFightingPhysicalAggression">) {
	return <BooleanField formState={formState} name="isIncidentFightingPhysicalAggression" label={label} {...props} />;
}

/**
 * Field component for the `isIncidentVerbalAbuseDisrespectingStudent` field of the StudentBehaviorNote model.
 */
export function IsIncidentVerbalAbuseDisrespectingStudent({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentVerbalAbuseDisrespectingStudent,
	...props
}: FieldProps<"isIncidentVerbalAbuseDisrespectingStudent">) {
	return (
		<BooleanField formState={formState} name="isIncidentVerbalAbuseDisrespectingStudent" label={label} {...props} />
	);
}

/**
 * Field component for the `isIncidentDisrepectingTeacher` field of the StudentBehaviorNote model.
 */
export function IsIncidentDisrepectingTeacher({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentDisrepectingTeacher,
	...props
}: FieldProps<"isIncidentDisrepectingTeacher">) {
	return <BooleanField formState={formState} name="isIncidentDisrepectingTeacher" label={label} {...props} />;
}

/**
 * Field component for the `isIncidentDisruption` field of the StudentBehaviorNote model.
 */
export function IsIncidentDisruption({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentDisruption,
	...props
}: FieldProps<"isIncidentDisruption">) {
	return <BooleanField formState={formState} name="isIncidentDisruption" label={label} {...props} />;
}

/**
 * Field component for the `isIncidentDefianceOrNonCompliance` field of the StudentBehaviorNote model.
 */
export function IsIncidentDefianceOrNonCompliance({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentDefianceOrNonCompliance,
	...props
}: FieldProps<"isIncidentDefianceOrNonCompliance">) {
	return <BooleanField formState={formState} name="isIncidentDefianceOrNonCompliance" label={label} {...props} />;
}

/**
 * Field component for the `isIncidentOther` field of the StudentBehaviorNote model.
 */
export function IsIncidentOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isIncidentOther,
	...props
}: FieldProps<"isIncidentOther">) {
	return <BooleanField formState={formState} name="isIncidentOther" label={label} {...props} />;
}

/**
 * Field component for the `incidentOther` field of the StudentBehaviorNote model.
 */
export function IncidentOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.incidentOther,
	...props
}: FieldProps<"incidentOther">) {
	return <TextField formState={formState} name="incidentOther" optional label={label} multiLine {...props} />;
}

/**
 * Field component for the `isMotivationAttentionSeeking` field of the StudentBehaviorNote model.
 */
export function IsMotivationAttentionSeeking({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationAttentionSeeking,
	...props
}: FieldProps<"isMotivationAttentionSeeking">) {
	return <BooleanField formState={formState} name="isMotivationAttentionSeeking" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationReaction` field of the StudentBehaviorNote model.
 */
export function IsMotivationReaction({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationReaction,
	...props
}: FieldProps<"isMotivationReaction">) {
	return <BooleanField formState={formState} name="isMotivationReaction" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationAnger` field of the StudentBehaviorNote model.
 */
export function IsMotivationAnger({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationAnger,
	...props
}: FieldProps<"isMotivationAnger">) {
	return <BooleanField formState={formState} name="isMotivationAnger" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationFrustration` field of the StudentBehaviorNote model.
 */
export function IsMotivationFrustration({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationFrustration,
	...props
}: FieldProps<"isMotivationFrustration">) {
	return <BooleanField formState={formState} name="isMotivationFrustration" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationHabit` field of the StudentBehaviorNote model.
 */
export function IsMotivationHabit({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationHabit,
	...props
}: FieldProps<"isMotivationHabit">) {
	return <BooleanField formState={formState} name="isMotivationHabit" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationLackOfSelfContol` field of the StudentBehaviorNote model.
 */
export function IsMotivationLackOfSelfContol({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationLackOfSelfContol,
	...props
}: FieldProps<"isMotivationLackOfSelfContol">) {
	return <BooleanField formState={formState} name="isMotivationLackOfSelfContol" label={label} {...props} />;
}

/**
 * Field component for the `isMotivationOther` field of the StudentBehaviorNote model.
 */
export function IsMotivationOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isMotivationOther,
	...props
}: FieldProps<"isMotivationOther">) {
	return <BooleanField formState={formState} name="isMotivationOther" label={label} {...props} />;
}

/**
 * Field component for the `motivationOther` field of the StudentBehaviorNote model.
 */
export function MotivationOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.motivationOther,
	...props
}: FieldProps<"motivationOther">) {
	return <TextField formState={formState} name="motivationOther" optional label={label} multiLine {...props} />;
}

/**
 * Field component for the `isConsequenceRecDirectorIntervention` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecDirectorIntervention({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecDirectorIntervention,
	...props
}: FieldProps<"isConsequenceRecDirectorIntervention">) {
	return <BooleanField formState={formState} name="isConsequenceRecDirectorIntervention" label={label} {...props} />;
}

/**
 * Field component for the `isConsequenceRecSenOrSelIntervention` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecSenOrSelIntervention({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecSenOrSelIntervention,
	...props
}: FieldProps<"isConsequenceRecSenOrSelIntervention">) {
	return <BooleanField formState={formState} name="isConsequenceRecSenOrSelIntervention" label={label} {...props} />;
}

/**
 * Field component for the `isConsequenceRecStudentServiceTeamIntervention` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecStudentServiceTeamIntervention({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecStudentServiceTeamIntervention,
	...props
}: FieldProps<"isConsequenceRecStudentServiceTeamIntervention">) {
	return (
		<BooleanField
			formState={formState}
			name="isConsequenceRecStudentServiceTeamIntervention"
			label={label}
			{...props}
		/>
	);
}

/**
 * Field component for the `isConsequenceRecParentContactFromReporter` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecParentContactFromReporter({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecParentContactFromReporter,
	...props
}: FieldProps<"isConsequenceRecParentContactFromReporter">) {
	return (
		<BooleanField formState={formState} name="isConsequenceRecParentContactFromReporter" label={label} {...props} />
	);
}

/**
 * Field component for the `isConsequenceRecParentContactFromSchoolDirector` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecParentContactFromSchoolDirector({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecParentContactFromSchoolDirector,
	...props
}: FieldProps<"isConsequenceRecParentContactFromSchoolDirector">) {
	return (
		<BooleanField
			formState={formState}
			name="isConsequenceRecParentContactFromSchoolDirector"
			label={label}
			{...props}
		/>
	);
}

/**
 * Field component for the `isConsequenceRecOther` field of the StudentBehaviorNote model.
 */
export function IsConsequenceRecOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecOther,
	...props
}: FieldProps<"isConsequenceRecOther">) {
	return <BooleanField formState={formState} name="isConsequenceRecOther" label={label} {...props} />;
}

/**
 * Field component for the `consequenceRecOther` field of the StudentBehaviorNote model.
 */
export function ConsequenceRecOther({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.consequenceRecOther,
	...props
}: FieldProps<"consequenceRecOther">) {
	return <TextField formState={formState} name="consequenceRecOther" optional label={label} multiLine {...props} />;
}

/**
 * Field component for the `additionalComments` field of the StudentBehaviorNote model.
 */
export function AdditionalComments({
	formState,
	label = StudentBehaviorNoteFormat.fieldLabels.additionalComments,
	...props
}: FieldProps<"additionalComments">) {
	return <TextField formState={formState} name="additionalComments" label={label} optional {...props} />;
}

export namespace StudentNote {
	export type SelectProps = FieldProps<"studentNoteID"> &
		Omit<StudentNoteSelectField.SelectProps<StudentBehaviorNoteFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `studentNote` edge of the StudentBehaviorNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentNoteSelectField.Select formState={formState} name="studentNoteID" {...props} />;
	}
}
